import {
  CSSProperties,
  ReactElement,
  SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Box } from '@mui/material';
import { debugLog } from 'shared/logger/debugLog';
import { isRotated90Or270 } from '../model';
import { ResponsiveImageProps } from '../types';

type Dimensions = {
  width: number;
  height: number;
};

function resolveWrapperStyle(
  imageDimensions: Dimensions | null,
  rotated: boolean
): CSSProperties {
  if (!imageDimensions) {
    return {
      position: 'relative',
    };
  }

  if (rotated) {
    return {
      width: `${imageDimensions.height}px`,
      height: `${imageDimensions.width}px`,
      position: 'relative',
    };
  }

  return {
    width: `${imageDimensions.width}px`,
    height: `${imageDimensions.height}px`,
    position: 'relative',
  };
}

function resolveImageStyle(
  imageDimensions: Dimensions | null,
  imageRotation: number,
  rotated: boolean
): CSSProperties {
  if (!imageDimensions) {
    return {
      pointerEvents: 'auto',
      transform: `rotate(${imageRotation}deg)`,
    };
  }

  if (rotated) {
    const offsetX = (imageDimensions.height - imageDimensions.width) / 2;
    const offsetY = (imageDimensions.width - imageDimensions.height) / 2;

    return {
      pointerEvents: 'auto',
      width: `${imageDimensions.width}px`,
      height: `${imageDimensions.height}px`,
      transformOrigin: 'center center',
      transform: `translate(${offsetX}px, ${offsetY}px) rotate(${imageRotation}deg)`,
    };
  }

  return {
    pointerEvents: 'auto',
    width: `${imageDimensions.width}px`,
    height: `${imageDimensions.height}px`,
    transform: `rotate(${imageRotation}deg)`,
  };
}

export function ResponsiveImage({
  imageRef,
  imageSrc,
  handleImageOnLoad,
  onImageLoad,
  imageRotation,
  imageNaturalWidth,
  imageNaturalHeight,
}: ResponsiveImageProps): ReactElement {
  const [imageDimensions, setImageDimensions] = useState<Dimensions | null>(
    imageNaturalWidth > 0 && imageNaturalHeight > 0
      ? {
          width: imageNaturalWidth,
          height: imageNaturalHeight,
        }
      : null
  );

  useEffect(() => {
    if (imageNaturalWidth > 0 && imageNaturalHeight > 0) {
      setImageDimensions({
        width: imageNaturalWidth,
        height: imageNaturalHeight,
      });
    }
  }, [imageNaturalWidth, imageNaturalHeight]);

  const onLoad = useCallback(
    (event: SyntheticEvent<HTMLImageElement>) => {
      const image = event.currentTarget;
      const nextWidth = image.naturalWidth;
      const nextHeight = image.naturalHeight;

      setImageDimensions((prev) => {
        if (
          prev &&
          prev.width === nextWidth &&
          prev.height === nextHeight
        ) {
          return prev;
        }

        return {
          width: nextWidth,
          height: nextHeight,
        };
      });

      debugLog('handle image', image);
      handleImageOnLoad(image);

      if (typeof onImageLoad === 'function') {
        onImageLoad();
      }
    },
    [handleImageOnLoad, onImageLoad]
  );

  const rotated = isRotated90Or270(imageRotation);

  const wrapperStyle = useMemo(
    () => resolveWrapperStyle(imageDimensions, rotated),
    [imageDimensions, rotated]
  );

  const imageStyle = useMemo(
    () =>
      resolveImageStyle(
        imageDimensions,
        imageRotation ?? 0,
        rotated
      ),
    [imageDimensions, imageRotation, rotated]
  );

  return (
    <div style={wrapperStyle}>
      <Box
        component='img'
        crossOrigin='use-credentials'
        ref={imageRef}
        src={imageSrc}
        onLoad={onLoad}
        style={imageStyle}
      />
    </div>
  );
}
